/**
 * TrademarkNote — the nominative-fair-use note for any page that names a
 * competitor's device.
 *
 * Written to be read, not skipped. A defensive legal blob invites the question
 * "what are they hiding"; stating plainly why we name other brands does the
 * opposite, and it is the same thing the law asks for: identify the mark, take
 * only what is needed, and make clear there is no affiliation.
 *
 * Usage:  <TrademarkNote brands={["Morpheus8"]} />
 */

/** Mark → the entity that owns it. Attribution is part of the fair-use test. */
const OWNERS: Record<string, string> = {
  "Morpheus8": "InMode Ltd.",
  "Ultherapy": "Merz Aesthetics",
  "CoolSculpting": "Allergan Aesthetics, an AbbVie company",
  "BBL": "Sciton, Inc.",
  "BBL HERO": "Sciton, Inc.",
  "CoolPeel": "Cartessa Aesthetics",
  "Fraxel": "Solta Medical",
  "Clear + Brilliant": "Solta Medical",
  "Emsculpt NEO": "BTL Industries",
  "EmSculpt": "BTL Industries",
  "PicoSure": "Cynosure",
  "PicoWay": "Candela",
  "SculpSure": "Cynosure",
  "Sculptra": "Galderma",
  "Vivace": "Aesthetics Biomedical",
  "Thermage": "Solta Medical",
  "Sofwave": "Sofwave Medical",
};

function ownerOf(brand: string): string {
  return OWNERS[brand] ?? "its respective owner";
}

/** "A", "A and B", "A, B and C" */
function list(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export default function TrademarkNote({ brands }: { brands: string[] }) {
  if (!brands.length) return null;

  const attributions = brands.map((b) => `${b}® is a registered trademark of ${ownerOf(b)}`);
  const owners = Array.from(new Set(brands.map(ownerOf)));

  return (
    <aside
      aria-label="Trademark and affiliation notice"
      style={{
        maxWidth: 820,
        margin: "0 auto",
        padding: "18px 20px",
        borderLeft: "3px solid #5B7A9E",
        background: "#FAF7F2",
        borderRadius: 4,
        fontSize: ".84rem",
        lineHeight: 1.65,
        color: "#5A544E",
      }}
    >
      <strong style={{ display: "block", color: "#2C2C2C", marginBottom: 6, fontSize: ".86rem" }}>
        Why we name other devices
      </strong>
      <p style={{ margin: 0 }}>
        Patients ask about treatments by brand name, so we answer by brand name rather than talk
        around the question. {list(attributions)}. Lakewood Med Spa is not affiliated with, endorsed
        by, or sponsored by {list(owners)}, and does not offer {list(brands)}. Any comparison here
        describes the underlying technology; device design, settings, protocols, clinical evidence
        and individual results differ between systems, and a consultation with Dr. Kohli is required
        to determine what is appropriate for you.
      </p>
    </aside>
  );
}
